import type { FC, ReactNode } from 'react'
import type { AnnouncementSettings, ListingCard, ListingDetail } from '@/types/marketplace'
import type { HeaderState } from '@/lib/community/owner-hub'

import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
    AlertTriangle,
    ArrowRight,
    BadgeCheck,
    Building2,
    CalendarDays,
    ChevronRight,
    Clock,
    Eye,
    Flag,
    MapPin,
    Minus,
    Plus,
    ShieldCheck,
    Sparkles,
    Store
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { listingTypeLabel } from '@/lib/marketplace/format'
import { recordListingView } from '@/lib/analytics/client'
import { breadcrumbNode, jsonLdGraph, listingNode } from '@/lib/seo/jsonld'
import { listingSeoDescription, listingSeoTitle } from '@/lib/seo/metadata'
import { OG_HEIGHT, OG_WIDTH } from '@/lib/seo/images'

import { Seo } from '@/components/generals/seo'
import { SiteShell, Section } from '@/components/marketplace/site-shell'
import { ListingGallery } from '@/components/marketplace/listing-gallery'
import { ContactButton, ShareButton } from '@/components/marketplace/contact-button'
import { ReportDialog } from '@/components/marketplace/report-dialog'
import { ListingGrid } from '@/components/marketplace/listing-card'
import { CategoryIcon } from '@/components/marketplace/category-icon'
import { Button, buttonVariants } from '@/components/ui/button'
/**
 * The Bazio mark: a small square, black-hairlined plate in DM Sans caps. Red
 * always carries BLACK text, per the contrast note in design/design.json.
 */
const MARK_TONES = {
    red: 'bg-brand-red text-ink-900',
    muted: 'bg-surface-muted text-ink-900',
    paper: 'bg-paper text-ink-900',
    black: 'bg-ink-900 text-paper'
} as const

const Mark: FC<{
    children: ReactNode
    tone?: keyof typeof MARK_TONES
    title?: string
    className?: string
}> = ({ children, tone = 'muted', title, className }): ReactNode => {
    return (
        <span
            title={title}
            className={cn(
                'type-label mta-border inline-flex items-center gap-1 rounded-mta-xs px-2 py-1 text-[10px] leading-none',
                MARK_TONES[tone],
                className
            )}
        >
            {children}
        </span>
    )
}

/**
 * Everything a listing detail page is loaded with, exported because both routes
 * that render this view type their own `getServerSideProps` against it.
 */
export interface ListingPageProps {
    listing: ListingDetail
    related: Array<ListingCard>
    disclosure: string
    announcement: AnnouncementSettings
    baseUrl: string
    postedOn: string
    sellerSince: string
    lastUpdated: string
    /**
     * True when this listing is not public and is only being shown because the
     * verified Telegram session belongs to the seller who owns it.
     */
    ownerPreview: boolean
    header: HeaderState
}

interface ListingViewProps extends ListingPageProps {
    /**
     * Which marketplace's storefront this markup is rendering inside, as the
     * prefix from `storefrontBasePath`: `/c/<slug>` for every marketplace, the
     * founding one included. Every MARKETPLACE-relative link is written through it,
     * so one component tree links within whichever tenant loaded it. Platform
     * routes (/safety, /report, /my-listings) are deliberately NOT prefixed —
     * they are the same pages for every marketplace.
     */
    basePath?: string
    /** The marketplace's own name, for copy that would otherwise say "Bazio". */
    marketplaceName: string
    /**
     * Keep this page out of search regardless of the listing's state. An
     * unlisted tenant is reachable by direct link but must not be indexed.
     */
    noindex?: boolean
}

/**
 * Real policies for a contact-only marketplace. There is no checkout here, so
 * these cover how a deal actually happens rather than shipping and refunds.
 */
const POLICIES = [
    {
        label: 'Contacting the seller',
        body: 'You deal with the seller directly on Telegram. Bazio lists and moderates the offer, but is not a party to the conversation or to any agreement you reach.'
    },
    {
        label: 'Safety',
        body: 'Meet in a public place, inspect the item before you pay, and never send a deposit to someone you cannot verify. A verified badge confirms identity only, never that a deal is safe.'
    },
    {
        label: 'Reporting a problem',
        body: 'Anything misleading, prohibited or fraudulent can be reported without an account. Moderators review every report and can suspend a listing or a seller immediately.'
    }
]

/** Listing kinds where asking for more than one is meaningful. */
const QUANTITY_TYPES = ['selling']

const STATE_NOTICE: Record<string, { title: string, body: string }> = {
    pending_review: {
        title: 'This listing is waiting for a moderator',
        body: 'It is not on the marketplace yet, so nobody else can open this page. You will see it go live here once it is approved.'
    },
    draft: {
        title: 'This listing is still a draft',
        body: 'It has not been submitted for review, so nobody else can open this page.'
    },
    imported_draft: {
        title: 'This listing is still a draft',
        body: 'It has not been submitted for review, so nobody else can open this page.'
    },
    sold: {
        title: 'This listing is sold',
        body: 'The seller marked this as sold, so it is kept here for reference only. Browse the marketplace for live offers.'
    },
    expired: {
        title: 'This listing has expired',
        body: 'Listings expire automatically after a period of inactivity. The seller can relist it from the bot at any time.'
    },
    suspended: {
        title: 'This listing is suspended',
        body: 'A moderator removed this listing from the marketplace while it is reviewed. Contact details are hidden.'
    },
    archived: {
        title: 'This listing is archived',
        body: 'This listing is no longer part of the active marketplace.'
    },
    rejected: {
        title: 'This listing was not approved',
        body: 'It did not pass moderation and was never published to the community.'
    }
}

export const ListingView: FC<ListingViewProps> = ({
    listing,
    related,
    disclosure,
    announcement,
    baseUrl,
    postedOn,
    sellerSince,
    lastUpdated,
    ownerPreview,
    header,
    basePath = '',
    marketplaceName,
    noindex = false
}): ReactNode => {
    const [quantity, setQuantity] = useState(1)

    // Anything that is not live is "closed": the gallery dims, contact is
    // disabled and the page is kept out of search. That now also covers the
    // pre-publication states an owner can preview.
    const isClosed = listing.status !== 'active'
    const notice = STATE_NOTICE[listing.status]
    const canonical = baseUrl ? `${baseUrl}${basePath}/listing/${listing.slug}` : undefined

    /**
     * The founding storefront has a dedicated /category/<slug> page; a tenant
     * storefront does not, so its category link is the filtered browse view
     * instead of a promise into a 404.
     */
    const categoryHref = listing.categorySlug
        ? (basePath ? `${basePath}/browse?category=${listing.categorySlug}` : `/category/${listing.categorySlug}`)
        : null

    /**
     * EVERYTHING A CRAWLER SEES, built once from the same values the page
     * renders below.
     *
     * Three things it must never do, all of which it would be easy to do here:
     * translate the seller's words, restate a fact the listing does not carry,
     * or describe the listing differently from the visible page. So the title,
     * the description and the structured data are all derived from `listing`
     * itself, and the price quoted to a search engine is `priceLabel` — the
     * exact string rendered a few hundred lines below.
     */
    const seo = useMemo(() => {
        const communityName = listing.community?.name ?? marketplaceName
        const url = canonical ?? `${basePath}/listing/${listing.slug}`

        return {
            title: listingSeoTitle({
                title: listing.title,
                price: listing.price,
                priceType: listing.priceType,
                priceLabel: listing.priceLabel,
                communityName
            }),
            description: listingSeoDescription({
                title: listing.title,
                description: listing.description,
                communityName,
                location: listing.location
            }),
            /*
             * The generated card rather than the raw photo. A seller's
             * photograph is any shape at all, and a portrait one pasted into
             * Telegram is cropped to a letterbox strip; the card puts that same
             * photograph into the 1200x630 frame every crawler expects, beside
             * the price and the marketplace.
             */
            ogImage: `/api/og/listing/${listing.slug}`,
            jsonLd: jsonLdGraph([
                listingNode({
                    title: listing.title,
                    description: listing.description,
                    url,
                    images: listing.media.map((item) => item.storageUrl),
                    price: listing.price,
                    currency: listing.currency,
                    priceType: listing.priceType,
                    listingType: listing.listingType,
                    categoryName: listing.categoryName,
                    categorySlug: listing.categorySlug,
                    status: listing.status,
                    publishedAt: listing.publishedAt,
                    communityName,
                    sellerName: listing.seller.displayName,
                    inLanguage: listing.sourceLanguage
                }),
                // The same trail the visitor can see, immediately below.
                breadcrumbNode(baseUrl, [
                    { name: communityName || 'Home', path: basePath || '/' },
                    ...(listing.categoryName && categoryHref
                        ? [{ name: listing.categoryName, path: categoryHref }]
                        : []),
                    { name: listing.title }
                ])
            ])
        }
    }, [listing, marketplaceName, canonical, basePath, baseUrl, categoryHref])

    /**
     * There is no per-tenant seller route yet, and the global `/seller/...` one
     * resolves against the FOUNDING marketplace, so it is only ever right for a
     * listing that belongs to that marketplace. Asking the listing's own
     * community, rather than the prefix of the page rendering it, is what keeps
     * the link on the founding marketplace's own listings now that they live
     * under a prefix like everyone else's. No link is better than a wrong one —
     * the button below is guarded on this being null.
     */
    const sellerResolvesHere = listing.community ? listing.community.isPrimary : basePath === ''

    const sellerHref = sellerResolvesHere && listing.seller.telegramUsername
        ? `/seller/${listing.seller.telegramUsername}`
        : null

    /*
     * A HANDLE, not merely a contact. A permalink reaches the post rather than
     * a chat window, so there is nowhere for a prepared enquiry to be pasted.
     */
    const hasTelegramHandle = listing.telegramContact?.kind === 'handle'

    // A quantity stepper only makes sense on goods for sale. It would be
    // nonsense on a job, a service or a wanted ad.
    //
    // It also needs somewhere to GO: the number's only job is to shape the
    // enquiry copied to the clipboard as Telegram opens, so with no reachable
    // handle it is a control that does nothing, sitting in a two-column grid
    // beside a one-line unavailable notice.
    const showQuantity = QUANTITY_TYPES.includes(listing.listingType) && !isClosed && hasTelegramHandle

    // Where "see all" goes, and one honest line about how the row was picked.
    const browseMoreHref = categoryHref ?? `${basePath}/browse`

    const relatedNote = [
        'Live offers picked on a close price',
        listing.location ? `sellers around ${listing.location}` : null,
        listing.categoryName ? 'and the same category' : null
    ]
        .filter(Boolean)
        .join(', ')
        .concat('.')

    const enquiry = useMemo(() => {
        const amount = showQuantity && quantity > 1 ? ` (x${quantity})` : ''

        return `Hi, I saw "${listing.title}"${amount} for ${listing.priceLabel} on ${marketplaceName}${canonical ? ` - ${canonical}` : ''}. Is it still available?`
    }, [listing.title, listing.priceLabel, canonical, quantity, showQuantity, marketplaceName])

    /**
     * One view per page load, counted with NO deduplication: the same person
     * refreshing a hundred times adds a hundred views, which is exactly what
     * the counter is meant to report.
     *
     * The ref keeps a re-render, a Suspense replay or React's development
     * double-effect from counting the SAME load twice; it is keyed on the
     * listing id, so navigating to another listing and back counts again.
     * The response carries the new total, so the number on screen moves with
     * the visit that just happened instead of lagging a load behind.
     */
    const [viewCount, setViewCount] = useState(listing.viewCount)
    const countedFor = useRef<string | null>(null)

    useEffect(() => {
        setViewCount(listing.viewCount)
    }, [listing.id, listing.viewCount])

    useEffect(() => {
        // An owner checking their own unpublished listing is not a visit.
        if (ownerPreview) return
        if (countedFor.current === listing.id) return

        countedFor.current = listing.id

        let active = true

        void recordListingView(listing.id, listing.sellerId).then((total) => {
            if (active && typeof total === 'number') setViewCount(total)
        })

        return (): void => {
            active = false
        }
    }, [listing.id, listing.sellerId, ownerPreview])

    return (
        <SiteShell disclosure={disclosure} announcement={announcement} className='pb-24 lg:pb-0' {...header}>
            <Seo
                title={seo.title}
                description={seo.description}
                canonical={canonical}
                baseUrl={baseUrl}
                ogType='product'
                /*
                 * The generated card, which puts the seller's own photograph on
                 * the left and the price, category and marketplace on the
                 * right. Safe to point at unconditionally: the route degrades to
                 * the seller's own photograph if the card will not draw, and
                 * only to the Bazio lockup when the listing has no photo or is
                 * no longer public.
                 *
                 * The dimensions describe the CARD, which is what this URL
                 * serves. They are declared rather than omitted because a
                 * correctly-sized 1200x630 is the normal response and Telegram
                 * lays out better knowing it; the photo-fallback path is a
                 * fault, and a real photograph in a slightly wrong box still
                 * tells a buyer what is for sale, which the lockup does not.
                 */
                ogImage={seo.ogImage}
                ogImageAlt={listing.title}
                ogImageWidth={OG_WIDTH}
                ogImageHeight={OG_HEIGHT}
                /*
                 * The seller's language, not the site's. A listing written in
                 * Albanian is Albanian to a crawler too, which is what stops a
                 * search engine treating it as badly-written English.
                 */
                locale={listing.sourceLanguage ?? undefined}
                noindex={isClosed || noindex}
                jsonLd={seo.jsonLd}
            />

            <Section className='py-6 sm:py-8'>
                <nav aria-label='Breadcrumb' className='mb-6'>
                    <ol className='type-label flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-text-muted'>
                        <li>
                            <Link
                                href={basePath || '/'}
                                className='mta-focus mta-scroll-safe rounded-mta-xs transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:text-ink-900'
                            >
                                Home
                            </Link>
                        </li>

                        <li aria-hidden='true' className='flex items-center'>
                            <ChevronRight className='size-3.5 text-brand-red' />
                        </li>

                        {listing.categorySlug && categoryHref ? (
                            <>
                                <li>
                                    <Link
                                        href={categoryHref}
                                        className='mta-focus mta-scroll-safe rounded-mta-xs transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:text-ink-900'
                                    >
                                        {listing.categoryName}
                                    </Link>
                                </li>

                                <li aria-hidden='true' className='flex items-center'>
                                    <ChevronRight className='size-3.5 text-brand-red' />
                                </li>
                            </>
                        ) : null}

                        <li className='max-w-[16rem] truncate text-ink-900' aria-current='page'>
                            {listing.title}
                        </li>
                    </ol>
                </nav>

                {ownerPreview && (
                    <div className='mta-border mb-6 flex flex-col gap-4 rounded-mta-sm bg-surface-muted p-5 sm:flex-row sm:items-center sm:justify-between'>
                        <p className='text-sm leading-relaxed text-ink-900'>
                            <span className='type-label mr-2 text-[11px]'>Owner preview:</span>
                            only you can see this page.
                        </p>

                        <div className='flex flex-wrap gap-2.5'>
                            <Link
                                href={`/my-listings/${listing.id}`}
                                className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-focus mta-scroll-safe mta-border h-11 rounded-mta-xs bg-paper px-4 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                            >
                                Edit listing
                            </Link>

                            <Link
                                href='/my-listings'
                                className={cn(buttonVariants({ variant: 'ghost' }), 'type-label mta-focus mta-scroll-safe h-11 rounded-mta-xs px-4 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                            >
                                Back to my listings
                            </Link>
                        </div>
                    </div>
                )}

                {notice && (
                    <div className='mta-border mb-6 flex items-start gap-4 rounded-mta-sm bg-paper p-5'>
                        <span
                            aria-hidden='true'
                            className='mta-border inline-flex size-10 shrink-0 items-center justify-center rounded-mta-xs bg-brand-red'
                        >
                            <AlertTriangle className='size-5 text-ink-900' />
                        </span>

                        <div>
                            <p className='type-display text-lg text-ink-900'>
                                {notice.title}
                            </p>

                            <p className='mt-1.5 max-w-2xl text-sm leading-relaxed text-text-muted'>
                                {notice.body}
                            </p>
                        </div>
                    </div>
                )}

                {/* Explicit row/column placement so the DOM order stays
                    image -> product info -> description, which is the right
                    order when this collapses to one column on mobile. */}
                <div className='grid gap-10 lg:grid-cols-[minmax(0,1.5fr)_minmax(370px,0.95fr)] lg:grid-rows-[auto_1fr] lg:gap-x-14 lg:gap-y-12'>
                    <div className='min-w-0 lg:col-start-1 lg:row-start-1'>
                        <ListingGallery
                            media={listing.media}
                            title={listing.title}
                            placeholder={listing.placeholder}
                            categorySlug={listing.categorySlug}
                            seed={listing.id}
                            dimmed={isClosed}
                        />
                    </div>

                    {/* ---------------- Product information ---------------- */}
                    <div className='min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2'>
                        {/* Never give this column its own scroll container. It is
                            taller than a laptop viewport once the seller block is
                            in, so a capped, scrollable box here produced a second
                            vertical scrollbar on desktop. The page uses ONE normal
                            browser scroll; this column simply flows. */}
                        <div>
                            <div className='flex flex-wrap items-center gap-2'>
                                <Mark tone='muted'>
                                    {listingTypeLabel(listing.listingType)}
                                </Mark>

                                {listing.isFeatured && (
                                    <Mark tone='red' title='Paid placement. This listing was promoted.'>
                                        <Sparkles className='size-3' aria-hidden='true' />
                                        Promoted
                                    </Mark>
                                )}

                                {listing.categoryName && (
                                    <Link
                                        href={`${basePath}/browse?category=${listing.categorySlug}`}
                                        className='type-label mta-border mta-focus mta-scroll-safe inline-flex min-h-7 items-center gap-1.5 rounded-mta-xs bg-paper px-2 py-1 text-[10px] leading-none text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'
                                    >
                                        <CategoryIcon name={listing.categoryIcon} className='size-3.5' />
                                        {listing.categoryName}
                                    </Link>
                                )}
                            </div>

                            {/* Seller-written and often long, so it stays DM Sans and sentence case. */}
                            <h1 className='mt-5 text-[28px] font-bold leading-[1.08] tracking-[-0.02em] text-ink-900 sm:text-[34px] lg:text-[38px]'>
                                {listing.title}
                            </h1>

                            <span aria-hidden='true' className='mt-5 block h-[3px] w-12 bg-brand-red' />

                            <p className='type-display mt-4 text-[38px] leading-none text-ink-900 sm:text-[44px]'>
                                {listing.priceLabel}
                            </p>

                            <div className='mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-text-muted'>
                                <span className='inline-flex items-center gap-1.5'>
                                    <Clock className='size-3.5' aria-hidden='true' />
                                    {`Updated ${lastUpdated}`}
                                </span>

                                {listing.location && (
                                    <span className='inline-flex items-center gap-1.5'>
                                        <MapPin className='size-3.5' aria-hidden='true' />
                                        {listing.location}
                                    </span>
                                )}

                                <span className='inline-flex items-center gap-1.5'>
                                    <Eye className='size-3.5' aria-hidden='true' />
                                    {`${viewCount} ${viewCount === 1 ? 'view' : 'views'}`}
                                </span>
                            </div>

                            {/* ---------------- Quantity + primary action ---------------- */}
                            <div className={cn(
                                'mt-7 grid gap-2.5',
                                showQuantity ? 'grid-cols-[116px_minmax(0,1fr)]' : 'grid-cols-1'
                            )}>
                                {showQuantity && (
                                    <div className='mta-border grid h-13 grid-cols-[38px_minmax(0,1fr)_38px] items-center overflow-hidden rounded-mta-xs bg-paper'>
                                        <button
                                            type='button'
                                            aria-label='Decrease quantity'
                                            onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                                            disabled={quantity <= 1}
                                            className='mta-focus grid h-full place-items-center text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink-900'
                                        >
                                            <Minus className='size-4' aria-hidden='true' />
                                        </button>

                                        <span className='text-center text-sm font-bold tabular-nums text-ink-900' aria-live='polite'>
                                            {quantity}
                                        </span>

                                        <button
                                            type='button'
                                            aria-label='Increase quantity'
                                            onClick={() => setQuantity((value) => Math.min(99, value + 1))}
                                            className='mta-focus grid h-full place-items-center text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'
                                        >
                                            <Plus className='size-4' aria-hidden='true' />
                                        </button>
                                    </div>
                                )}

                                <ContactButton
                                    listingId={listing.id}
                                    sellerId={listing.sellerId}
                                    contact={listing.telegramContact}
                                    disabled={isClosed}
                                    disabledReason={isClosed ? 'Contact is disabled for closed listings.' : undefined}
                                    enquiry={enquiry}
                                    className='type-label mta-focus mta-scroll-safe h-13 rounded-mta-xs text-[11px]'
                                />
                            </div>

                            <div className='mt-2.5 grid gap-2.5 sm:grid-cols-2'>
                                <ShareButton
                                    title={listing.title}
                                    listingId={listing.id}
                                    className='type-label mta-focus mta-scroll-safe mta-border h-11 w-full whitespace-nowrap rounded-mta-xs bg-paper text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'
                                />

                                <ReportDialog
                                    listingId={listing.id}
                                    listingTitle={listing.title}
                                    sellerId={listing.sellerId}
                                    trigger={(
                                        <Button
                                            variant='outline'
                                            className='type-label mta-focus mta-scroll-safe mta-border h-11 w-full whitespace-nowrap rounded-mta-xs bg-paper px-3 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'
                                        >
                                            <Flag className='size-4' aria-hidden='true' />
                                            Report listing
                                        </Button>
                                    )}
                                />
                            </div>

                            {/* ---------------- Store policies ---------------- */}
                            <p className='type-eyebrow mt-10 flex items-center gap-2.5 text-text-muted'>
                                <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                                All store policies
                            </p>

                            <div className='mt-4 border-y-[1.5px] border-ink-900'>
                                {POLICIES.map((policy) => (
                                    <details key={policy.label} className='group border-b-[1.5px] border-ink-900 last:border-b-0'>
                                        <summary className='type-label mta-focus mta-scroll-safe flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 text-[11px] text-ink-900 marker:content-none'>
                                            {policy.label}
                                            <Plus className='size-5 shrink-0 stroke-[1.75] text-brand-red transition-transform duration-[var(--dur)] ease-[var(--ease-brand)] group-open:rotate-45' aria-hidden='true' />
                                        </summary>

                                        <p className='pb-5 pr-8 text-sm leading-6 text-text-muted'>
                                            {policy.body}
                                        </p>
                                    </details>
                                ))}
                            </div>

                            {/* ---------------- Listed in ---------------- */}
                            {listing.community && (
                                <div className='mt-10'>
                                    <p className='type-eyebrow flex items-center gap-2.5 text-text-muted'>
                                        <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                                        Listed in
                                    </p>

                                    <div className='mta-border mt-4 rounded-mta-sm bg-paper p-5'>
                                        <div className='flex items-start gap-3.5'>
                                            {listing.community.logoUrl ? (
                                                // The marketplace's name sits beside the mark as real
                                                // text, so the mark itself is decorative.
                                                // eslint-disable-next-line @next/next/no-img-element
                                                <img
                                                    src={listing.community.logoUrl}
                                                    alt=''
                                                    width={96}
                                                    height={96}
                                                    loading='lazy'
                                                    decoding='async'
                                                    className='mta-border size-12 shrink-0 rounded-mta-xs bg-white object-cover'
                                                />
                                            ) : (
                                                <span
                                                    aria-hidden='true'
                                                    className='mta-border flex size-12 shrink-0 items-center justify-center rounded-mta-xs bg-ink-900 text-paper'
                                                >
                                                    <Store className='size-5' />
                                                </span>
                                            )}

                                            <div className='min-w-0 flex-1'>
                                                <p className='truncate font-bold text-ink-900'>
                                                    {listing.community.name}
                                                </p>

                                                <p className='text-sm text-text-muted'>
                                                    Community marketplace
                                                </p>

                                                {/*
                                                  * The marketplace's own handle is an ADDRESS, never a
                                                  * proof of anything. Printed only when it has one.
                                                  */}
                                                {listing.community.telegramHandle && (
                                                    <p className='truncate text-sm text-text-muted'>
                                                        {`@${listing.community.telegramHandle.replace(/^@/, '')}`}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {listing.community.href && (
                                            <Link
                                                href={listing.community.href}
                                                className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-focus mta-scroll-safe mta-border mt-5 h-11 w-full rounded-mta-xs bg-paper text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                                            >
                                                View marketplace
                                                <ArrowRight className='size-4' aria-hidden='true' />
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ---------------- Seller ---------------- */}
                            <div className='mt-10'>
                                <p className='type-eyebrow flex items-center gap-2.5 text-text-muted'>
                                    <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                                    Seller
                                </p>

                                <div className='mta-border mt-4 rounded-mta-sm bg-paper p-5'>
                                    <div className='flex items-start gap-3.5'>
                                        <span
                                            aria-hidden='true'
                                            className='type-label mta-border flex size-12 shrink-0 items-center justify-center rounded-mta-xs bg-ink-900 text-base text-paper'
                                        >
                                            {listing.seller.displayName.trim().charAt(0).toUpperCase() || 'M'}
                                        </span>

                                        <div className='min-w-0 flex-1'>
                                            <p className='truncate font-bold text-ink-900'>
                                                {listing.seller.businessName || listing.seller.displayName}
                                            </p>

                                            {/*
                                              * The resolved handle, not the raw seller column, so a
                                              * listing ingested from Telegram shows the poster's
                                              * handle instead of nothing. Rendered with the `@`;
                                              * the link is built from the bare form.
                                              */}
                                            {listing.telegramContact?.username && (
                                                <p className='truncate text-sm text-text-muted'>
                                                    {`@${listing.telegramContact.username}`}
                                                </p>
                                            )}

                                            <div className='mt-2.5 flex flex-wrap gap-1.5'>
                                                {listing.seller.isVerified && (
                                                    <Mark
                                                        tone='red'
                                                        title='Telegram identity confirmed by Bazio. This is not a guarantee of any transaction.'
                                                    >
                                                        <BadgeCheck className='size-3' aria-hidden='true' />
                                                        Verified
                                                    </Mark>
                                                )}

                                                {listing.seller.isBusiness && (
                                                    <Mark tone='muted'>
                                                        <Building2 className='size-3' aria-hidden='true' />
                                                        Business
                                                    </Mark>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <dl className='mt-5 grid grid-cols-3 gap-2.5'>
                                        <div className='mta-border rounded-mta-xs bg-surface-muted px-3 py-2.5'>
                                            <dt className='type-eyebrow text-text-muted'>
                                                Since
                                            </dt>

                                            <dd className='mt-1 text-sm font-bold text-ink-900'>
                                                {sellerSince}
                                            </dd>
                                        </div>

                                        <div className='mta-border rounded-mta-xs bg-surface-muted px-3 py-2.5'>
                                            <dt className='type-eyebrow text-text-muted'>
                                                Active
                                            </dt>

                                            <dd className='mt-1 text-sm font-bold text-ink-900'>
                                                {listing.seller.activeListings}
                                            </dd>
                                        </div>

                                        <div className='mta-border rounded-mta-xs bg-surface-muted px-3 py-2.5'>
                                            <dt className='type-eyebrow text-text-muted'>
                                                Done
                                            </dt>

                                            <dd className='mt-1 text-sm font-bold text-ink-900'>
                                                {listing.seller.completedListings}
                                            </dd>
                                        </div>
                                    </dl>

                                    {sellerHref && (
                                        <Link
                                            href={sellerHref}
                                            className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-focus mta-scroll-safe mta-border mt-5 h-11 w-full rounded-mta-xs bg-paper text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                                        >
                                            <Store className='size-4' aria-hidden='true' />
                                            View seller profile
                                        </Link>
                                    )}
                                </div>
                            </div>

                            <div className='mta-border mt-5 flex items-start gap-3 rounded-mta-sm bg-surface-muted p-4'>
                                <ShieldCheck className='mt-0.5 size-4 shrink-0 text-ink-900' aria-hidden='true' />

                                <p className='text-xs leading-relaxed text-text-muted'>
                                    {disclosure}
                                    {' '}
                                    <Link
                                        href='/safety'
                                        className='mta-focus mta-scroll-safe rounded-mta-xs font-bold text-ink-900 underline underline-offset-2'
                                    >
                                        Read the safety guide
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ---------------- Description ---------------- */}
                    <div className='min-w-0 lg:col-start-1 lg:row-start-2'>
                        <p className='type-eyebrow flex items-center gap-2.5 text-text-muted'>
                            <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                            The details
                        </p>

                        <h2 className='type-display mt-3 text-3xl text-ink-900 sm:text-4xl'>
                            Description
                        </h2>

                        <p className='mt-5 max-w-2xl whitespace-pre-line text-[0.95rem] leading-7 text-text-muted'>
                            {listing.description || 'The seller did not add a description.'}
                        </p>

                        <dl className='mt-9 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                            <div className='mta-border rounded-mta-xs bg-paper p-4'>
                                <dt className='type-eyebrow text-text-muted'>
                                    Category
                                </dt>

                                <dd className='mt-2 flex items-center gap-1.5 text-sm font-bold text-ink-900'>
                                    <CategoryIcon name={listing.categoryIcon} className='size-4' />
                                    {listing.categoryName ?? 'Uncategorised'}
                                </dd>
                            </div>

                            <div className='mta-border rounded-mta-xs bg-paper p-4'>
                                <dt className='type-eyebrow text-text-muted'>
                                    Location
                                </dt>

                                <dd className='mt-2 flex items-center gap-1.5 text-sm font-bold text-ink-900'>
                                    <MapPin className='size-4' aria-hidden='true' />
                                    {listing.location ?? listing.seoCountry?.name ?? 'Not specified'}
                                </dd>

                                {listing.seoCountry && (
                                    <dd className='mt-1.5'>
                                        <Link
                                            href={listing.seoCountry.path}
                                            className='mta-focus inline-flex min-h-6 items-center text-xs font-semibold text-text-muted underline-offset-4 hover:text-ink-900 hover:underline'
                                        >
                                            {`More listings in ${listing.seoCountry.name}`}
                                        </Link>
                                    </dd>
                                )}
                            </div>

                            <div className='mta-border rounded-mta-xs bg-paper p-4'>
                                <dt className='type-eyebrow text-text-muted'>
                                    Posted
                                </dt>

                                <dd className='mt-2 flex items-center gap-1.5 text-sm font-bold text-ink-900'>
                                    <CalendarDays className='size-4' aria-hidden='true' />
                                    {postedOn}
                                </dd>
                            </div>

                            <div className='mta-border rounded-mta-xs bg-paper p-4'>
                                <dt className='type-eyebrow text-text-muted'>
                                    Views
                                </dt>

                                <dd className='mt-2 flex items-center gap-1.5 text-sm font-bold text-ink-900'>
                                    <Eye className='size-4' aria-hidden='true' />
                                    {viewCount}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </Section>

            {related.length > 0 && (
                <Section className='pb-16'>
                    <div className='flex flex-wrap items-end justify-between gap-4 border-b-[1.5px] border-ink-900 pb-5'>
                        <div className='max-w-2xl'>
                            <p className='type-eyebrow flex items-center gap-2.5 text-text-muted'>
                                <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                                Keep looking
                            </p>

                            <h2 className='type-display mt-3 text-3xl text-ink-900 sm:text-4xl'>
                                {listing.categoryName ? `More in ${listing.categoryName}` : 'More like this'}
                            </h2>

                            <p className='mt-3 text-sm leading-relaxed text-text-muted'>
                                {relatedNote}
                            </p>
                        </div>

                        <Link
                            href={browseMoreHref}
                            className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-focus mta-scroll-safe mta-border h-11 rounded-mta-xs bg-paper px-5 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                        >
                            {listing.categorySlug ? 'See all' : 'Browse all'}
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>

                    <ListingGrid listings={related} className='mt-7' priorityCount={0} basePath={basePath} />
                </Section>
            )}

            {/* ---------------- Sticky mobile CTA ---------------- */}
            <div className='fixed inset-x-0 bottom-0 z-40 border-t-2 border-ink-900 bg-paper px-4 pt-3 pb-safe lg:hidden'>
                <div className='mx-auto flex max-w-lg items-center gap-3'>
                    <div className='min-w-0 flex-1'>
                        <p className='type-display truncate text-2xl leading-none text-ink-900'>
                            {listing.priceLabel}
                        </p>

                        <p className='mt-1.5 truncate text-xs text-text-muted'>
                            {listing.seller.displayName}
                        </p>
                    </div>

                    <div className='w-[58%] shrink-0'>
                        <ContactButton
                            listingId={listing.id}
                            sellerId={listing.sellerId}
                            contact={listing.telegramContact}
                            disabled={isClosed}
                            className='type-label mta-focus rounded-mta-xs text-[11px]'
                        />
                    </div>
                </div>
            </div>
        </SiteShell>
    )
}

export default ListingView
